import type { CanvasQuestionGroup, CanvasTheme } from "@/api/openapi/discovery/types.gen";

export interface CanvasOverviewThemeItem {
  group: CanvasTheme;
  groupIndex: number;
}

export interface CanvasOverviewSection {
  questionId: string | null;
  questionLabel: string | null;
  questionNumber: number | null;
  themes: CanvasOverviewThemeItem[];
}

export function buildCanvasOverviewSections(
  themes: CanvasTheme[],
  questionGroups: CanvasQuestionGroup[],
): CanvasOverviewSection[] {
  const themeItems = themes.map((group, groupIndex) => ({ group, groupIndex }));
  const knownQuestionIds = new Set(questionGroups.map((group) => group.question_id));
  const hasMatchingTheme = themeItems.some(({ group }) =>
    Boolean(group.question_id && knownQuestionIds.has(group.question_id)),
  );

  if (!hasMatchingTheme) {
    return [
      {
        questionId: null,
        questionLabel: null,
        questionNumber: null,
        themes: themeItems,
      },
    ];
  }

  const groupedSections: CanvasOverviewSection[] = questionGroups.flatMap((questionGroup, questionIndex) => {
    const matchingThemes = themeItems.filter(({ group }) => group.question_id === questionGroup.question_id);
    if (matchingThemes.length === 0) return [];

    return [
      {
        questionId: questionGroup.question_id,
        questionLabel: questionGroup.label,
        questionNumber: questionIndex + 1,
        themes: matchingThemes,
      },
    ];
  });
  const unmatchedThemes = themeItems.filter(
    ({ group }) => !group.question_id || !knownQuestionIds.has(group.question_id),
  );

  if (unmatchedThemes.length > 0) {
    groupedSections.push({
      questionId: null,
      questionLabel: null,
      questionNumber: null,
      themes: unmatchedThemes,
    });
  }

  return groupedSections;
}
